import * as React from 'react';
import {connect, useDispatch, useSelector} from 'react-redux';
import {withRouter} from 'react-router-dom';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import {formValueSelector} from 'redux-form';
// @ts-expect-error
import capitalize from 'underscore.string/capitalize';

import {getMultipleEntityType} from 'nutshell-core/utils/get-multiple-entity-type';
import {getSingularEntityType} from 'nutshell-core/utils/get-singular-entity-type';

import {CustomizeFieldsHeader} from 'shells/customize-fields/customize-fields-header';
import {Dialog} from 'shells/dialog/dialog';
import {CustomizeButton} from 'shells/modal/customize-button';
import {Modal} from 'shells/modal/modal';

import {useGetSessionUser} from '../core/use-get-session-user';
import {EntityForm} from '../entity-form/entity-form';
import {
    LEADS_FORM_KEY,
    NUM_LEADS_CREATING,
    IS_CREATING_SINGLE_LEAD_FROM_MULTIPLE,
    CREATOR_ORIGIN_ID,
} from '../entity-form/forms/lead-form-connected';
import {handleEntitySave} from '../entity-form/handle-entity-save';
import {FeatureGateModalWrapper} from '../limits/feature-gates/feature-gate-modal-wrapper';
import {LeadLimitModal} from '../limits/lead-limit-modal';
import {useGetLimitsByType} from '../limits/use-get-limits-by-type';
// @ts-expect-error
import * as dashboardActions from '../master-dashboard/dashboard-actions';

import {CustomizeFieldsWrapper} from './customize-fields/customize-fields-wrapper';
import {EntityFormSidebar} from './entity-form-sidebar/entity-form-sidebar';
import type {EntityFormType} from './types';

import styles from './entity-modal.module.css';

// @ts-expect-error
const $ = window.jQuery;

const MODAL_TITLES = {
    accounts: 'Add a company',
    mcfxcompanies: 'Add a company',
    activities: 'Schedule an activity',
    contacts: 'Add a person',
    leads: 'Add a lead',
    tasks: 'Create a task',
    quotes: 'Add a quote',
    invoices: 'Add an invoice',
} as const;

const MODAL_WIDTH = 500;

// @ts-expect-error
const mapStateToProps = (state): StateProps => {
    const leadFormSelector = formValueSelector(LEADS_FORM_KEY);

    return {
        isPristine: true, // TODO: make this work, hook into form state
        leadFormValues: leadFormSelector(
            state,
            NUM_LEADS_CREATING, // The number of leads being created, always >1
            IS_CREATING_SINGLE_LEAD_FROM_MULTIPLE, // True if single lead is being created from multiple list selection
            CREATOR_ORIGIN_ID // The creatorOriginId after bulk leads are successfully created, else undefined
        ),
    };
};

type OwnProps = {
    entityType: EntityFormType | null | undefined;
    entityId: string | null | undefined;
    isOpen: boolean;
    onClose: () => void;
    disabled?: boolean;
};

type StateProps = {
    isPristine: boolean;
    leadFormValues: any;
};

type RoutedProps = OwnProps & ContextRouter;

type Props = StateProps & RoutedProps;

function EntityModalComponent(props: Props) {
    const {onClose, history} = props;

    const [showConfigModal, setIsShowingConfigModal] = React.useState<boolean>(false);
    const [showGatedModal, setIsShowingGatedModal] = React.useState<boolean>(false);
    const [showLeadLimitModal, setShowLeadLimitModal] = React.useState<boolean>(false);
    const [isSaving, setIsSaving] = React.useState<boolean>(false);
    const [confirmDismissalPromise, setConfirmDismissalPromise] = React.useState<
        JQueryDeferred<any> | null | undefined
    >(null);

    const {user, isLoading} = useGetSessionUser();
    const {limits} = useGetLimitsByType('LEADS');
    const dispatch = useDispatch();

    React.useEffect(() => {
        if (props.entityType === 'leads' && limits && !limits.canAdd) {
            setShowLeadLimitModal(true);
        }
    }, [limits, props.entityType]);

    React.useEffect(() => {
        if (props.entityType === 'accounts') {
            dispatch(dashboardActions.fetchAccountsSchema());
        }

        if (props.entityType === 'leads') {
            dispatch(dashboardActions.fetchLeadsSchema());
        }

        if (props.entityType === 'contacts') {
            dispatch(dashboardActions.fetchContactsSchema());
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.entityType]);

    // The only time the modal can be closed by clicking the outside is when the lead form has
    // been successfully submitted (the creatorOriginId has a value)
    const canClickOutsideToClose = Boolean(props.leadFormValues[CREATOR_ORIGIN_ID]);

    const shouldShowCustomizeButton =
        user &&
        user.permissions.canAccessSetup &&
        !showConfigModal &&
        !showGatedModal &&
        (props.entityType === 'leads' ||
            props.entityType === 'contacts' ||
            props.entityType === 'accounts');

    const handleConfigureFieldsClose = () => {
        setIsShowingConfigModal(false);
    };

    const selector = formValueSelector(LEADS_FORM_KEY);
    // @ts-expect-error
    const selectedContacts = useSelector((state) => selector(state, 'links.contacts'));
    // @ts-expect-error
    const selectedAccounts = useSelector((state) => selector(state, 'links.accounts'));
    // @ts-expect-error
    const relatedEntity = useSelector((state) => selector(state, 'relatedEntity'));

    const numLeadsCreating =
        relatedEntity === 'contacts'
            ? selectedContacts && selectedContacts.length
            : selectedAccounts && selectedAccounts.length;

    const getEntityModalHeader = (
        entityType: EntityFormType | null | undefined,
        isCreatingSingleLeadFromMultiple: boolean | null | undefined,
        isConfiguringFields: boolean
    ): string | React.ReactNode => {
        // This should not happen
        if (!entityType) {
            return 'Create';
        }

        if (isConfiguringFields) {
            return (
                <CustomizeFieldsHeader
                    breadcrumbText={MODAL_TITLES[entityType]}
                    onCloseCustomizeModal={handleConfigureFieldsClose}
                />
            );
        }

        // Return a different title if creating multiple leads from multiple peeps
        if (
            entityType === 'leads' &&
            numLeadsCreating &&
            numLeadsCreating > 1 &&
            !isCreatingSingleLeadFromMultiple
        ) {
            return `Create ${numLeadsCreating} leads`;
        }

        return props.entityId
            ? capitalize(getSingularEntityType(entityType))
            : MODAL_TITLES[entityType];
    };

    const handleCancel = () => {
        if (props.entityId) {
            props.history.replace({
                pathname: props.location.pathname.replace(/\/edit/, ''),
                state: {modal: true},
            });
        } else {
            props.onClose();
        }
    };

    const handleBeforeClose = () => {
        if (!props.entityId || props.isPristine) return;

        const newConfirmDismissalPromise = new $.Deferred();
        setConfirmDismissalPromise(newConfirmDismissalPromise);

        return confirmDismissalPromise;
    };

    const handleDiscardChanges = () => {
        if (confirmDismissalPromise) {
            confirmDismissalPromise.resolve(true);
            setConfirmDismissalPromise(null);
        }
    };

    const handleCancelClose = () => {
        if (confirmDismissalPromise) {
            confirmDismissalPromise.resolve(false);
            setConfirmDismissalPromise(null);
        }
    };

    const handleSave = React.useCallback(
        (entity, jumpToUrl) => {
            setIsSaving(true);
            handleEntitySave(entity, {
                jumpToUrl,
                onClose: onClose,
                history: history,
            });
        },
        [history, onClose]
    );

    // Note: This is called as a side-effect whenever the Modal below
    // is closed, even from an external source.
    const handleClose = React.useCallback(() => {
        // Reset state of config modal to closed
        setIsShowingConfigModal(false);
        setShowLeadLimitModal(false);
        setIsShowingGatedModal(false);
        if (isSaving) {
            setIsSaving(false);
        } else {
            onClose();
        }
    }, [onClose, isSaving, setIsSaving]);

    let actionButton;
    if (isLoading) {
        actionButton = null;
    } else {
        actionButton = NutClientConfig.hasCustomCreateNewEntity ? (
            <CustomizeButton onClick={() => setIsShowingConfigModal(true)} gated={false} />
        ) : (
            <CustomizeButton onClick={() => setIsShowingGatedModal(true)} gated={true} />
        );
    }

    return (
        <>
            <Modal
                // @ts-expect-error
                headerText={getEntityModalHeader(
                    props.entityType,
                    props.leadFormValues[IS_CREATING_SINGLE_LEAD_FROM_MULTIPLE],
                    showConfigModal
                )}
                headerSubtext={
                    showConfigModal && props.entityType
                        ? `Select which fields will be visible to everyone on your account when adding new ${getMultipleEntityType(
                              props.entityType
                          )}`
                        : undefined
                }
                isOpen={props.isOpen}
                canClickOutsideToClose={canClickOutsideToClose}
                onBeforeClose={handleBeforeClose}
                actionButton={shouldShowCustomizeButton ? actionButton : null}
                onClose={handleClose}
                maxWidth={MODAL_WIDTH}
                visibleOverflow={true}
                className={styles.modal}
            >
                {showConfigModal && props.entityType ? (
                    <CustomizeFieldsWrapper
                        entityType={props.entityType}
                        onClose={handleConfigureFieldsClose}
                    />
                ) : (
                    <>
                        <EntityForm
                            entityType={props.entityType}
                            entityId={props.entityId}
                            disabled={Boolean(props.disabled)}
                            onCancelClick={handleCancel}
                            onSave={handleSave}
                        />
                        {(props.entityType === 'contacts' || props.entityType === 'accounts') && (
                            <EntityFormSidebar entityType={props.entityType} />
                        )}
                    </>
                )}
                {confirmDismissalPromise ? (
                    <Dialog
                        anchor={null}
                        location='top'
                        question='Are you sure you want to discard your changes?'
                        onPrimary={handleDiscardChanges}
                        onSecondary={handleCancelClose}
                    />
                ) : undefined}
                {showLeadLimitModal && (
                    <LeadLimitModal
                        planLimit={limits && limits.currentPlanLimit}
                        onClose={handleClose}
                    />
                )}
            </Modal>
            <FeatureGateModalWrapper
                isOpen={showGatedModal}
                onClose={() => setIsShowingGatedModal(false)}
                title='Customize your data collection'
                description='Tailor the fields that appear and are required when adding new leads, contacts, or companies to gather exactly the information your business needs.'
                bullets={[
                    'Select only the fields that matter to your business, so your team can quickly capture the right information.',
                    'Rearrange fields in a way that fits your team’s workflow, making it easy to enter data.',
                    'Choose which fields to mark as required, so your team always remembers to fill in key details.',
                ]}
                feature='customizableCreateNew'
            />
        </>
    );
}

// @ts-expect-error
const connector = connect<Props, RoutedProps, _, _, _, _>(mapStateToProps);
// @ts-expect-error
export const EntityModal = withRouter<OwnProps>(connector(EntityModalComponent));
